import { useState, useRef, useEffect } from 'react'
import { Button } from "@/components/ui/button"
import { Slider } from "@/components/ui/slider"
import { Play, Pause, SkipBack, SkipForward } from "lucide-react"
import { getVideoFrameRate } from '@/lib/utils'
import { useVideoWithAudit } from '@/features/videos/use-video-audit'
import { updateVideoThumbnail } from '@/features/videos/videos-store'

interface VideoPreviewProps {
  videoSrc: string
  videoName: string
  videoId?: string
  thumbnailUrl?: string
  className?: string
}

export function VideoPreview({ videoSrc, videoName, videoId, thumbnailUrl, className = "" }: VideoPreviewProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentFrame, setCurrentFrame] = useState(0)
  const [totalFrames, setTotalFrames] = useState(0)
  const [duration, setDuration] = useState(0)
  const [fps, setFps] = useState(30)
  const [isMetadataLoaded, setIsMetadataLoaded] = useState(false)
  const [showControls, setShowControls] = useState(false)
  const { logVideoAction } = useVideoWithAudit()
  const thumbnailGeneratedRef = useRef(false)
  
  // Track if user has started interaction to optimize loading
  const [hasStarted, setHasStarted] = useState(false)

  // Render current frame to canvas
  const renderCurrentFrame = () => {
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Set canvas size to match video dimensions
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    
    // Draw the current frame
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    
    // Log thumbnail generation (only once per preview)
    if (!thumbnailGeneratedRef.current) {
      logVideoAction('thumbnail_generated', `Thumbnail generated for ${videoName}`, {
        videoName,
        videoId: videoId || videoName,
        width: video.videoWidth,
        height: video.videoHeight,
        timestamp: video.currentTime
      }, { toast: false })
      
      // If we don't have a thumbnail URL provided, and use has an ID, save this generated thumbnail
      // This caches the thumbnail on the server so we don't need to generate it next time
      if (!thumbnailUrl && videoId) {
        // Generate a lower quality/resolution thumbnail for storage
        const quality = 0.6;
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        
        // This is a "fire and forget" operation - we don't want to block the UI
        updateVideoThumbnail(videoId, dataUrl)
          .then(() => console.debug(`Thumbnail cached for video ${videoId}`))
          .catch(err => console.error('Failed to auto-save thumbnail:', err));
      }
      
      thumbnailGeneratedRef.current = true
    }
  }

  // Handle video metadata loaded
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handleMetadataLoaded = () => {
      const videoDuration = video.duration
      const videoFps = getVideoFrameRate(video) || 30
      const frames = Math.floor(videoDuration * videoFps)
      
      setDuration(videoDuration)
      setFps(videoFps)
      setTotalFrames(frames)
      setCurrentFrame(0)
      setIsMetadataLoaded(true)
      
      // Draw the first frame
      video.currentTime = 0
      setTimeout(() => renderCurrentFrame(), 100)
    }

    const handleTimeUpdate = () => {
      if (video.currentTime && fps && duration) {
        const frame = Math.floor(video.currentTime * fps)
        setCurrentFrame(frame)
        renderCurrentFrame()
      }
    }

    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)
    const handleEnded = () => {
      setIsPlaying(false)
      video.currentTime = 0
      setCurrentFrame(0)
      renderCurrentFrame()
    }

    video.addEventListener('loadedmetadata', handleMetadataLoaded)
    video.addEventListener('timeupdate', handleTimeUpdate)
    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)
    video.addEventListener('ended', handleEnded)

    return () => {
      video.removeEventListener('loadedmetadata', handleMetadataLoaded)
      video.removeEventListener('timeupdate', handleTimeUpdate)
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
      video.removeEventListener('ended', handleEnded)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fps, duration])

  // Toggle play/pause
  const togglePlayPause = () => {
    const video = videoRef.current
    if (!video) return

    if (!hasStarted) {
      setHasStarted(true)
      // Small delay to allow video to start loading if preload was none
      setTimeout(() => {
        video.play().catch(e => console.error("Play failed", e))
      }, 50)
      return
    }

    if (isPlaying) {
      video.pause()
    } else {
      video.play()
    }
  }

  // Handle frame slider change
  const handleFrameChange = (values: number[]) => {
    if (!hasStarted) setHasStarted(true);
    
    const video = videoRef.current
    if (!video || !duration || !fps) return

    const targetFrame = values[0]
    const targetTime = targetFrame / fps
    
    video.currentTime = Math.min(targetTime, duration)
    setCurrentFrame(targetFrame)
  }

  // Skip frames
  const skipFrames = (frameCount: number) => {
    if (!hasStarted) setHasStarted(true);
    
    const video = videoRef.current
    if (!video || !fps) return

    const newFrame = Math.max(0, Math.min(totalFrames - 1, currentFrame + frameCount))
    const newTime = newFrame / fps
    
    video.currentTime = Math.min(newTime, duration)
    setCurrentFrame(newFrame)
  }
  
  // Decide whether to show the static thumbnail or the canvas/video
  const showStaticThumbnail = thumbnailUrl && (!hasStarted && !isPlaying);

  return (
    <div 
      className={`relative group ${className}`}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
    >
      {/* Hidden video element */}
      <video
        ref={videoRef}
        src={videoSrc}
        className="hidden"
        preload={thumbnailUrl ? "none" : "metadata"}
        muted
        crossOrigin="anonymous"
      />
      
      {/* Static thumbnail image if available */}
      {showStaticThumbnail && (
        <img 
          src={thumbnailUrl} 
          alt={videoName}
          className="w-full h-full object-cover bg-muted rounded absolute inset-0 z-10" 
        />
      )}
      
      {/* Canvas for displaying frames - hidden if showing static thumbnail */}
      <canvas
        ref={canvasRef}
        className="w-full h-full object-cover bg-muted rounded"
        style={{ aspectRatio: '16/9', display: showStaticThumbnail ? 'none' : 'block' }}
      />
      
      {/* Play button overlay (always visible when not playing) */}
      {!isPlaying && (
        <div className="absolute inset-0 flex items-center justify-center z-20">
          <Button
            size="lg"
            className="rounded-full bg-background/80 backdrop-blur-sm hover:bg-background/90"
            onClick={togglePlayPause}
          >
            <Play size={24} />
          </Button>
        </div>
      )}
      
      {/* Control overlay (shows on hover when playing or paused) */}
      {(isMetadataLoaded || hasStarted) && (showControls || !isPlaying) && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-background/80 to-transparent p-4 z-20">
          {/* Timeline slider */}
          <div className="mb-3">
            <Slider
              value={[currentFrame]}
              max={Math.max(0, totalFrames - 1)}
              step={1}
              onValueChange={handleFrameChange}
              className="w-full"
            />
            <div className="flex justify-between items-center mt-1 text-xs text-muted-foreground">
              <span>Frame: {currentFrame + 1}</span>
              <span>Total: {totalFrames}</span>
            </div>
          </div>
          
          {/* Control buttons */}
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => skipFrames(-10)}
              disabled={currentFrame === 0}
            >
              <SkipBack size={16} />
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={togglePlayPause}
            >
              {isPlaying ? <Pause size={16} /> : <Play size={16} />}
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => skipFrames(10)}
              disabled={currentFrame >= totalFrames - 1}
            >
              <SkipForward size={16} />
            </Button>
          </div>
        </div>
      )}
      
      {/* Loading state */}
      {((!thumbnailUrl && !isMetadataLoaded) || (hasStarted && !isMetadataLoaded)) && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
        </div>
      )}
    </div>
  )
}
